import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { customStyles } from "../Common/DataTableStyles";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useUserContext } from "../../../Contexts/UserProvider";
import { toast } from "react-toastify";
// @ts-ignore
import "react-toastify/dist/ReactToastify.css";
import { Button } from "../../../components/ui/button";
import AddProductCategoryDialog from "./AddProductCategoryDialog"
import ProductCategoryDetailView from "./ProductCategoryDetailView";
import { Search, Plus, Loader2, Edit, Trash, EyeIcon, MoreVertical } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Input } from "../../../components/ui/input"
import ConfirmDialog from "../Modals/ConfirmationModal";
import { useCategoryManagement } from "../../Services/CategoryManagement"
import { isApiError, getApiErrorMessage, getApiErrorStatus } from "../../../Utilities/apiResponse";

const ProductCategory = () => {
    const [isAddProductOpen, setIsAddProductOpen] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState("")

    const [isLoading, setIsLoading] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState<number | null>(null);

    const [categoryDetails, setCategoryDetails] = useState({
        data: null,
        isShow: false
    });
    const [confirmationModal, setConfirmationModal] = useState({
        isShow: false,
        onConfirm: () => { },
    });
    const [selectedCategory, setSelectedCategory] = useState(null);
    const { getCategories, deleteCategory } = useCategoryManagement();
    const { tenant } = useUserContext();

    const columns = useMemo(() => [
        {
            name: "Category Name",
            selector: (row: any) => row?.categoryName,
            sortable: true,
            cell: (row: any) => (
                <div className="font-medium">
                    {row?.categoryName}
                    {row?.isSystemField && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-900 text-blue-100">
                            System
                        </span>
                    )}
                </div>
            ),
        },
        {
            name: "Description",
            selector: (row: any) => row?.description,
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row: any, index: number) => (
                <div className="flex justify-center w-full">
                    <DropdownMenu modal={false} onOpenChange={(isOpen) => { setIsDropdownOpen(isOpen ? index : null) }} open={isDropdownOpen === index}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-black border-gray-800 text-white">
                            <DropdownMenuItem className="hover:bg-gray-800 focus:bg-gray-800" onClick={() => handleViewDetails(row, true)}>
                                <EyeIcon className="h-4 w-4 mr-2" /> View
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                                className="hover:bg-gray-800 focus:bg-gray-800"
                                onClick={() => handleUpdateType(row)}
                                disabled={row?.isSystemField}
                            >
                                <Edit className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-gray-800" />
                            <DropdownMenuItem 
                                className="hover:bg-gray-800 focus:bg-gray-800 text-red-500"
                                onClick={() => handleConfirmationModal(true, () => handleDeleteType(row))} 
                                disabled={row?.isSystemField}
                            >
                                <Trash className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            center: true,
        },
    ], [isDropdownOpen]);

    useEffect(() => {
        handleLoadCategories();
    }, [])

    const openAddProduct = () => {
        setSelectedCategory(null);
        setIsUpdate(false);
        setIsDropdownOpen(null);
        setIsAddProductOpen(true);
    };

    const closeAddProduct = () => {
        setIsAddProductOpen(false);
        setIsDropdownOpen(null);
    };

    const handleLoadCategories = () => {
        setIsLoading(true);
        
        const res = getCategories();
        if (res && typeof res.then === 'function') {
            res.then((response: any) => {
                setCategories(Array.isArray(response) ? response : []);
                setIsLoading(false);
            }).catch((error: any) => {
                console.log("Error", error);
                setIsLoading(false);
            });
        } else {
             setCategories([]);
             setIsLoading(false);
        }
    };

    const handleDeleteType = (item: any) => {
        if (item?.isSystemField) {
            toast.warning("System fields cannot be deleted");
            return;
        }
        const res = deleteCategory(item?.id);
        if (res && typeof res.then === 'function') {
            res.then((response: any) => {
                if (isApiError(response)) {
                    const status = getApiErrorStatus(response);
                    if (status === 404) {
                        toast.info("Category not found or already deleted");
                        handleLoadCategories();
                        return;
                    }
                    toast.error(getApiErrorMessage(response) || "Failed to delete this item");
                    return;
                }
                toast.success("Category Deleted");
                handleLoadCategories();
            }).catch((error: any) => {
                toast.error(getApiErrorMessage(error) || "Failed to delete this item");
                console.log(error);
            });
        }
    }

    const handleUpdateType = (item: any) => {
        if (item?.isSystemField) {
            toast.warning("System fields cannot be updated");
            return;
        }
        setSelectedCategory(item);
        setIsUpdate(true);
        setIsDropdownOpen(null);
        setIsAddProductOpen(true);
    }

    const handleViewDetails = (item: any, isOpen: boolean) => {
        setIsDropdownOpen(null);
        setCategoryDetails(prev => ({
            data: item !== undefined && item !== null ? item : prev.data,
            isShow: isOpen
        }));
    }

    const handleConfirmationModal = (isOpen: boolean, onConfirm: any) => {
        setIsDropdownOpen(null);
        setConfirmationModal({
            isShow: isOpen,
            onConfirm: onConfirm,
        });
    };

    // Filter based on search query
    const filteredCategories = categories.filter(
        (cat) =>
            (cat?.categoryName?.toLowerCase().includes(searchQuery.toLowerCase()) || "") ||
            (cat?.description?.toLowerCase().includes(searchQuery.toLowerCase()) || "")
    )


    return (
        <div className="w-full mx-auto p-6 space-y-6 product-types">
            <div className="container mx-auto p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Manage Product Category</h1>
                    <Button
                        className="bg-purple-600 hover:bg-purple-700 transition-colors"
                        onClick={openAddProduct}
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Category
                    </Button>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <Input
                                placeholder="Search by name or description..."
                                className="pl-10"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-black rounded-lg shadow overflow-hidden border border-gray-800">
                    <DataTable
                        columns={columns}
                        data={filteredCategories}
                        pagination
                        progressPending={isLoading}
                        progressComponent={
                            <div className="flex items-center justify-center p-10">
                                <Loader2 className="animate-spin h-8 w-8 text-white" />
                            </div>
                        }
                        noDataComponent={
                            <div className="text-center py-10 text-gray-400">
                                No categories found
                            </div>
                        }
                        customStyles={customStyles}
                        highlightOnHover
                        pointerOnHover
                        responsive
                    />
                </div>

                <AddProductCategoryDialog
                    isOpen={isAddProductOpen}
                    onClose={closeAddProduct}
                    selectedCategory={selectedCategory}
                    onSuccessCallBack={handleLoadCategories}
                    isUpdate={isUpdate}
                    setIsUpdate={setIsUpdate}
                />

                <ProductCategoryDetailView category={categoryDetails?.data} open={categoryDetails?.isShow} onOpenChange={(isOpen) => handleViewDetails(null, isOpen)} />
            </div>

            <ConfirmDialog isShow={confirmationModal?.isShow} setIsDialogOpen={setConfirmationModal} title="Are you sure to continue ?" description="" confirmText="Delete" cancelText="Cancel" onConfirm={confirmationModal?.onConfirm} />
        </div>
    );
};

export default ProductCategory;
